import type { FC } from "react";
import { useCallback, useRef } from "react";

import { useInteractOutside } from "@react-aria/interactions";

import type { IconName } from "@similarweb/icons";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { AutocompleteWebCategories } from "pro-features/components/AutocompleteWebCategories/AutocompleteWebCategories";
import { ETabs } from "pro-features/components/AutocompleteWebCategories/types";

import type { ICategoryModalProps } from "../types";

interface IIndustrySearchProps {
    onItemClick: (item: ICategory) => void;
    onClose: () => void;
    showOnlyCategories?: boolean;
    showOnlyCustom?: boolean;
    placeholder?: string;
    categoryModalProps?: ICategoryModalProps;
    isPartnerPage?: boolean;
    customResolveItemIcon?: (item: ICategory, isChild: boolean) => IconName;
    selectedCategory?: ICategory | ICustomCategory | null;
    disabledCategories?: (ICategory | ICustomCategory)[];
    isCompare?: boolean;
    hasSelectedCategoryWithSegments?: boolean;
}

export const IndustryAnalysisSearch: FC<IIndustrySearchProps> = ({
    onItemClick,
    onClose,
    showOnlyCategories,
    showOnlyCustom,
    placeholder,
    categoryModalProps,
    isPartnerPage,
    customResolveItemIcon,
    selectedCategory,
    disabledCategories = [],
    isCompare = false,
    hasSelectedCategoryWithSegments,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useInteractOutside({
        ref: containerRef,
        onInteractOutside: (e) => {
            const target = e.target as Element;
            const isWithinQueryBar = target.closest(".QueryBarAutoComplete");
            const isWithinCustomModal = target.closest(".customCategoriesWizard");
            const isWithinCountryDropdown = target.closest(".market-builder-filter");

            if (!isWithinQueryBar && !isWithinCustomModal && !isWithinCountryDropdown) {
                onClose();
            }
        },
    });

    const handleItemClick = useCallback(
        (item: ICategory) => {
            onItemClick(item);
        },
        [onItemClick],
    );

    const getDefaultSelectedTab = (): ETabs => {
        if (!selectedCategory?.isCustomCategory) {
            return ETabs.PRE_DEFINED;
        }

        return (selectedCategory as ICustomCategory)?.isShared ? ETabs.SHARED : ETabs.CUSTOM;
    };

    return (
        <div ref={containerRef}>
            <AutocompleteWebCategories
                customResolveItemIcon={customResolveItemIcon}
                className="QueryBarAutoComplete"
                onClick={handleItemClick}
                showOnlyCategories={showOnlyCategories}
                autocompleteProps={{
                    placeholder: placeholder,
                    isFocused: true,
                    autoFocus: true,
                }}
                defaultSelectedTab={getDefaultSelectedTab()}
                showOnlyCustom={showOnlyCustom}
                categoryModalProps={categoryModalProps}
                isPartnerPage={isPartnerPage}
                onCloseCustomCategoriesModal={onClose}
                disabledCategories={disabledCategories}
                isCompare={isCompare}
                hasSelectedCategoryWithSegments={hasSelectedCategoryWithSegments}
            />
        </div>
    );
};
